import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPin, CheckCircle, ChevronLeft, Share2, Phone, Send, Loader2, Star, X, Crown,
} from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { StarRating } from '../components/StarRating';
import api from '../lib/api';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';

function whatsappLink(telefone, nome) {
  const limpo = (telefone || '').replace(/\D/g, '');
  if (limpo.length < 10) return null;
  const numero = limpo.startsWith('55') ? limpo : `55${limpo}`;
  const msg = encodeURIComponent(`Olá ${nome || ''}, encontrei seu perfil no GuiaCit e gostaria de tirar uma dúvida.`);
  return `https://wa.me/${numero}?text=${msg}`;
}

function ClienteAuthModal({ onClose, onSuccess }) {
  const { loginCliente, registerCliente } = useAuth();
  const [modo, setModo] = useState('login');
  const [form, setForm] = useState({ nome: '', email: '', senha: '' });
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErro(''); setLoading(true);
    try {
      if (modo === 'login') await loginCliente(form.email, form.senha);
      else await registerCliente(form);
      onSuccess?.();
    } catch (err) {
      setErro(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-bold text-gray-900" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {modo === 'login' ? 'Entrar para avaliar' : 'Cadastro rápido'}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16} className="text-gray-500" /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-3">
          {modo === 'register' && (
            <input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Seu nome" data-testid="cli-nome-input" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
          )}
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="cli-email-input" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
          <input type="password" required minLength={6} value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })} placeholder="Senha (mín. 6)" data-testid="cli-senha-input" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
          {erro && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{erro}</div>}
          <button type="submit" disabled={loading} data-testid="cli-submit-btn" className="w-full bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl py-2.5 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {loading && <Loader2 size={14} className="animate-spin" />} {modo === 'login' ? 'Entrar' : 'Criar conta'}
          </button>
          <p className="text-center text-xs text-gray-500">
            {modo === 'login' ? 'Ainda não tem conta?' : 'Já tem cadastro?'}{' '}
            <button type="button" onClick={() => setModo(modo === 'login' ? 'register' : 'login')} className="text-[#1D9E75] font-semibold">
              {modo === 'login' ? 'Cadastrar' : 'Entrar'}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}

function AvaliacaoForm({ profId, onCreated }) {
  const { isCliente } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [nota, setNota] = useState(5);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    if (!comentario.trim()) return;
    setEnviando(true);
    try {
      await api.post(`/profissionais/${profId}/avaliacoes`, { nota, comentario: comentario.trim() });
      setComentario(''); setNota(5);
      onCreated?.();
    } catch (err) {
      alert(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setEnviando(false); }
  };

  if (!isCliente) {
    return (
      <>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-sm text-gray-700 mb-2">Quer avaliar este profissional?</p>
          <p className="text-xs text-gray-500 mb-3">Faça um cadastro rápido (nome + email + senha)</p>
          <button onClick={() => setShowAuth(true)} data-testid="entrar-avaliar-btn" className="bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl px-4 py-2 text-sm transition-colors">
            Entrar / Cadastrar
          </button>
        </div>
        {showAuth && <ClienteAuthModal onClose={() => setShowAuth(false)} onSuccess={() => setShowAuth(false)} />}
      </>
    );
  }

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
      <p className="text-sm font-semibold text-gray-900">Deixe sua avaliação</p>
      <div className="flex items-center gap-1">
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" onClick={() => setNota(n)} className={n <= nota ? 'text-amber-500' : 'text-gray-300'}>
            <Star size={20} fill={n <= nota ? 'currentColor' : 'none'} />
          </button>
        ))}
        <span className="ml-2 text-xs text-gray-500">{nota}/5</span>
      </div>
      <textarea rows={3} value={comentario} onChange={(e) => setComentario(e.target.value)} placeholder="Conte sua experiência..." data-testid="aval-comentario-input"
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
      <button onClick={enviar} disabled={enviando || !comentario.trim()} data-testid="enviar-aval-btn"
        className="w-full bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl py-2 text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
        {enviando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar avaliação
      </button>
    </div>
  );
}

export default function ProfissionalPerfil() {
  const { slug } = useParams();
  const [prof, setProf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const carregar = () => {
    setLoading(true); setError(false);
    api.get(`/profissionais/slug/${slug}`)
      .then(res => setProf(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, [slug]);

  const handleShare = () => {
    if (navigator.share) navigator.share({ title: prof?.nome, url: window.location.href });
    else navigator.clipboard.writeText(window.location.href);
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA]"><Header /><div className="max-w-4xl mx-auto px-4 py-20 text-center"><Loader2 className="animate-spin mx-auto text-gray-400" /></div></div>;

  if (error || !prof) return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Profissional não encontrado</h2>
        <Link to="/buscar" className="btn-primary">Voltar à busca</Link>
      </div>
      <Footer />
    </div>
  );

  const { id, nome, bio, cidade, estado, avatarUrl, telefone, plano, servicos = [], fotos = [], avaliacoes = [], verificado, mediaAvaliacoes, totalAvaliacoes } = prof;
  const wa = whatsappLink(telefone, nome);
  const isPremium = plano === 'premium';

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link to="/buscar" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors">
          <ChevronLeft size={15} /> Voltar à busca
        </Link>

        <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm mb-6">
          <div className="flex flex-col sm:flex-row gap-5 items-start mb-6">
            <div className="relative flex-shrink-0">
              {avatarUrl ? (
                <img src={avatarUrl} alt={nome} className="w-24 h-24 rounded-2xl object-cover" />
              ) : (
                <div className="w-24 h-24 bg-[#E1F5EE] rounded-2xl flex items-center justify-center">
                  <span className="text-[#1D9E75] font-bold text-4xl">{nome?.[0]?.toUpperCase()}</span>
                </div>
              )}
              {verificado && (
                <div className="absolute -bottom-1 -right-1 bg-[#1D9E75] rounded-full p-1 shadow">
                  <CheckCircle size={14} className="text-white" />
                </div>
              )}
            </div>

            <div className="flex-1">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <h1 className="text-2xl font-bold text-[#085041] mb-1" style={{ fontFamily: "'Work Sans', sans-serif" }}>{nome}</h1>
                  <div className="flex items-center gap-2 flex-wrap">
                    {verificado && <span className="text-xs text-[#1D9E75] bg-[#E1F5EE] border border-[#9FE1CB] px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle size={10} /> Verificado</span>}
                    {isPremium && <span className="text-xs text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full flex items-center gap-1"><Crown size={10} /> Premium</span>}
                    <span className="flex items-center gap-1 text-xs text-gray-500"><MapPin size={11} /> {cidade}/{estado}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {totalAvaliacoes > 0 && (
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <StarRating rating={mediaAvaliacoes} size={14} />
                      <span>{mediaAvaliacoes} ({totalAvaliacoes})</span>
                    </div>
                  )}
                  <button onClick={handleShare} data-testid="share-btn" className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50">
                    <Share2 size={15} className="text-gray-500" />
                  </button>
                </div>
              </div>
              {bio && <p className="text-gray-600 text-sm mt-3 leading-relaxed">{bio}</p>}
            </div>
          </div>

          <div className="bg-[#E1F5EE] rounded-2xl p-4 border border-[#9FE1CB]">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex-1">
                <p className="font-semibold text-[#085041] text-sm">Entre em contato — gratuito</p>
                {telefone && <p className="text-[#0F6E56] text-xs mt-0.5">{telefone}</p>}
              </div>
              <div className="flex gap-2">
                {wa && (
                  <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-btn"
                    className="bg-[#25D366] hover:bg-[#1EBE57] text-white font-semibold py-2.5 px-4 rounded-xl text-sm flex items-center gap-2 transition-colors">
                    <Send size={14} /> WhatsApp
                  </a>
                )}
                {telefone && (
                  <a href={`tel:${telefone.replace(/\D/g, '')}`} data-testid="ligar-btn"
                    className="bg-white border border-[#1D9E75] text-[#1D9E75] hover:bg-[#1D9E75] hover:text-white font-semibold py-2.5 px-4 rounded-xl text-sm flex items-center gap-2 transition-colors">
                    <Phone size={14} /> Ligar
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 space-y-6">
            {servicos.length > 0 && (
              <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                <h2 className="font-semibold text-gray-900 mb-4">Serviços oferecidos</h2>
                <div className="space-y-3">
                  {servicos.map(s => (
                    <div key={s.id} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#1D9E75] mt-1.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{s.nome}</p>
                        {s.descricao && <p className="text-gray-500 text-xs mt-0.5">{s.descricao}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {fotos.length > 0 && (
              <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                <h2 className="font-semibold text-gray-900 mb-4">Portfólio de fotos</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {fotos.map(f => (
                    <div key={f.id} className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                      <img src={f.url} alt={f.descricao || ''} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm space-y-5">
              <h2 className="font-semibold text-gray-900">Avaliações ({totalAvaliacoes})</h2>
              {avaliacoes.length > 0 && (
                <div className="space-y-4">
                  {avaliacoes.slice().reverse().map(a => (
                    <div key={a.id} className="pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-medium text-gray-900 text-sm">{a.nomeCliente}</span>
                        <StarRating rating={a.nota} size={13} />
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">{a.comentario}</p>
                    </div>
                  ))}
                </div>
              )}
              <AvaliacaoForm profId={id} onCreated={carregar} />
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm">Localização</h3>
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin size={15} className="text-[#1D9E75]" />
                <span className="text-sm">{cidade}, {estado}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
